<script>
import { authComputed } from '@state/helpers'

export default {
  name: 'OrganizationAdminsForm',
  props: {
    submitError: {
      type: String,
      required: true,
    },
    organizationData: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      primaryAccountAdminName: '',
      primaryAccountAdminEmail: '',
      showPrimaryAccountAdminEmail: false,
      showPrimaryAccountAdminName: false,
      hasOtherAdmins: 0,
      otherAdmins: [{ adminName: '', adminEmail: '' }],
      errors: {
        primaryAccountAdminName: '',
        primaryAccountAdminEmail: '',
        hasOtherAdmins: '',
      },
      otherAdminsErrors: [{ adminName: '', adminEmail: '' }],
    }
  },
  computed: { ...authComputed },
  mounted() {
    // Prefill admin data from organization if available
    if (this.organizationData) {
      this.primaryAccountAdminName = this.organizationData.admin_name || ''
      this.primaryAccountAdminEmail = this.organizationData.admin_email || ''
    }
  },
  methods: {
    submitAdminsForm() {
      var adminsToSend = this.otherAdmins

      // Reset errors first
      this.errors = {
        primaryAccountAdminName: '',
        primaryAccountAdminEmail: '',
        hasOtherAdmins: '',
      }

      if (!this.primaryAccountAdminEmail) {
        this.errors.primaryAccountAdminEmail = 'Email required.'
        if (!this.showPrimaryAccountAdminEmail) {
          this.toggleShowPrimaryAccountAdminEmail()
        }
      } else if (!this.validEmail(this.primaryAccountAdminEmail)) {
        this.errors.primaryAccountAdminEmail = 'Valid email required.'
        if (!this.showPrimaryAccountAdminEmail) {
          this.toggleShowPrimaryAccountAdminEmail()
        }
      }
      if (!this.primaryAccountAdminName) {
        this.errors.primaryAccountAdminName = 'Primary admin name required.'
        if (!this.showPrimaryAccountAdminName) {
          this.toggleShowPrimaryAccountAdminName()
        }
      }

      // Only validate other admins if user selected "Yes"
      if (this.hasOtherAdmins) {
        this.validateOtherAdmins()
      } else {
        adminsToSend = []
        // Reset other admin errors when not needed
        this.otherAdminsErrors = [{ adminName: '', adminEmail: '' }]
      }

      // Check if there are any errors before submitting
      const hasMainErrors = Object.values(this.errors).some((x) => x !== '')
      const hasOtherAdminErrors =
        this.hasOtherAdmins &&
        Object.values(this.otherAdminsErrors).some(
          (x) => x.adminName !== '' || x.adminEmail !== ''
        )

      if (!hasMainErrors && !hasOtherAdminErrors) {
        this.$emit('clear-submit-errors')
        this.$emit('post-submit-admins', {
          primaryAccountAdminName: this.primaryAccountAdminName,
          primaryAccountAdminEmail: this.primaryAccountAdminEmail,
          hasOtherAdmins: this.hasOtherAdmins,
          otherAdmins: adminsToSend,
        })
      }
    },
    validateOtherAdmins() {
      this.otherAdmins.forEach((admin, index) => {
        if (!admin.adminName) {
          this.otherAdminsErrors[index].adminName = 'Admin name required'
        }
        if (!admin.adminEmail) {
          this.otherAdminsErrors[index].adminEmail = 'Admin email required'
        }
      })
    },
    clearSubmitErrors(fieldName) {
      if (fieldName) {
        this.errors[fieldName] = ''
      }
      this.$emit('clear-submit-errors')
    },
    clearOtherAdminsErrors(adminIndex, isName) {
      if (isName) {
        this.otherAdminsErrors[adminIndex].adminName = ''
        return
      }

      this.otherAdminsErrors[adminIndex].adminEmail = ''
    },
    validEmail: function (email) {
      const rule =
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
      return rule.test(email)
    },
    toggleShowPrimaryAccountAdminName() {
      this.showPrimaryAccountAdminName = !this.showPrimaryAccountAdminName
    },
    toggleShowPrimaryAccountAdminEmail() {
      this.showPrimaryAccountAdminEmail = !this.showPrimaryAccountAdminEmail
    },
    savePrimaryAccountAdminNameField() {
      if (!this.primaryAccountAdminName) {
        this.errors.primaryAccountAdminName = 'Admin name required.'
        return
      }
      this.toggleShowPrimaryAccountAdminName()
    },
    savePrimaryAccountAdminEmailField() {
      if (!this.primaryAccountAdminEmail) {
        this.errors.primaryAccountAdminEmail = 'Email required.'
        return
      } else if (!this.validEmail(this.primaryAccountAdminEmail)) {
        this.errors.primaryAccountAdminEmail = 'Valid email required.'
        return
      }
      this.toggleShowPrimaryAccountAdminEmail()
    },
    addOtherAdmin() {
      this.otherAdmins.push({ adminName: '', adminEmail: '' })
      this.otherAdminsErrors.push({ adminName: '', adminEmail: '' })
    },
  },
}
</script>

<template>
  <div class="">
    <div class="flex items-center mt-8 -mx-2">
      <div class="flex items-center gap-3">
        <div
          class="rounded-full text-center bg-blue text-white flex items-center justify-center"
          style="height: 35px; width: 35px"
          ><svg
            xmlns="http://www.w3.org/2000/svg"
            height="20px"
            viewBox="0 -960 960 960"
            width="20px"
            fill="white"
          >
            <path
              d="M382-240 154-468l57-57 171 171 367-367 57 57-424 424Z"
            /></svg
        ></div>
        <span class="text-blue">Info</span>
      </div>
      <div class="h-2px w-2/5 bg-gray ml-4 mr-4"></div>
      <div class="flex items-center gap-3">
        <div
          class="rounded-full text-center bg-blue text-white flex items-center justify-center"
          style="height: 35px; width: 35px"
          ><svg
            xmlns="http://www.w3.org/2000/svg"
            height="20px"
            viewBox="0 -960 960 960"
            width="20px"
            fill="white"
          >
            <path
              d="M382-240 154-468l57-57 171 171 367-367 57 57-424 424Z"
            /></svg
        ></div>
        <span class="text-blue">Social</span>
      </div>
      <div class="h-2px w-2/5 bg-gray ml-4 mr-4"></div>
      <div class="flex items-center gap-3">
        <p
          class="rounded-full text-center bg-blue text-white"
          style="line-height: 35px; width: 35px"
          >3</p
        >
        <span class="text-blue">Admins</span>
      </div>
    </div>
    <div class="flex -mx-2 mt-8 gap-10">
      <div class="flex flex-col items-start w-1/2">
        <div class="flex gap-3 justify-center mb-2">
          <label class="text-gray">Primary Account Admin: </label>
          <button
            v-show="!showPrimaryAccountAdminName"
            class="text-blue"
            @click="toggleShowPrimaryAccountAdminName"
            >Edit</button
          >
          <button
            v-show="showPrimaryAccountAdminName"
            class="text-blue"
            @click="savePrimaryAccountAdminNameField"
            >Save</button
          >
        </div>
        <span
          v-show="!showPrimaryAccountAdminName"
          class="text-left font-semibold"
          >{{
            primaryAccountAdminName === ''
              ? 'Admin Name'
              : primaryAccountAdminName
          }}</span
        >
        <div v-show="showPrimaryAccountAdminName" class="w-full relative">
          <input
            v-model="primaryAccountAdminName"
            type="text"
            name="admin-name"
            placeholder="Primary Account Admin"
            class="input"
            :class="[errors.primaryAccountAdminName !== '' ? 'border-red' : '']"
            @input="clearSubmitErrors('primaryAccountAdminName')"
          />
          <svg
            v-if="errors.primaryAccountAdminName !== ''"
            class="absolute right-0 top-0 mr-3 mt-2.5"
            xmlns="http://www.w3.org/2000/svg"
            width="17.433"
            height="15.57"
            viewBox="0 0 17.433 15.57"
          >
            <path
              class="a"
              fill="red"
              d="M19.066,17.847,12.211,5.978h0a1.808,1.808,0,0,0-.342-.483,1.772,1.772,0,0,0-2.505,0,1.715,1.715,0,0,0-.307.414h0L2.2,17.781v0a1.727,1.727,0,0,0-.246.535,1.769,1.769,0,0,0,1.764,2.229l13.707,0h0a1.77,1.77,0,0,0,1.636-2.7Zm-7.64.057a1.2,1.2,0,0,1-1.661,0,1.072,1.072,0,0,1-.354-.8,1.087,1.087,0,0,1,.354-.807,1.182,1.182,0,0,1,1.661,0,1.086,1.086,0,0,1,.354.807A1.07,1.07,0,0,1,11.426,17.9Zm.283-6.941c-.059.278-.136.6-.226.977-.086.352-.187.792-.3,1.32s-.229,1.178-.354,1.953h-.475q-.185-1.175-.355-1.958t-.3-1.311c-.079-.323-.15-.639-.216-.951a3.652,3.652,0,0,1-.1-.7,1.076,1.076,0,0,1,.351-.808,1.237,1.237,0,0,1,1.693,0,1.067,1.067,0,0,1,.362.808A3.393,3.393,0,0,1,11.709,10.963Z"
              transform="translate(-1.898 -4.978)"
            />
          </svg>
          <span
            v-if="errors.primaryAccountAdminName !== ''"
            class="right-0 absolute mr-1 text-red text-xxs capitalize text-right block w-full"
            >{{ errors.primaryAccountAdminName }}
          </span>
        </div>
      </div>
      <div class="flex flex-col items-start w-1/2">
        <div class="flex gap-3 justify-center mb-2">
          <label class="text-gray">Primary Admin Email: </label>
          <button
            v-show="!showPrimaryAccountAdminEmail"
            class="text-blue"
            @click="toggleShowPrimaryAccountAdminEmail"
            >Edit</button
          >
          <button
            v-show="showPrimaryAccountAdminEmail"
            class="text-blue"
            @click="savePrimaryAccountAdminEmailField"
            >Save</button
          >
        </div>
        <span
          v-show="!showPrimaryAccountAdminEmail"
          class="text-left font-semibold"
          >{{
            primaryAccountAdminEmail === ''
              ? 'name@company.com'
              : primaryAccountAdminEmail
          }}</span
        >
        <div v-show="showPrimaryAccountAdminEmail" class="w-full relative">
          <input
            v-model="primaryAccountAdminEmail"
            type="text"
            name="admin-email"
            placeholder="Admin Email"
            class="input"
            :class="[
              errors.primaryAccountAdminEmail !== '' ? 'border-red' : '',
            ]"
            @input="clearSubmitErrors('primaryAccountAdminEmail')"
          />
          <svg
            v-if="errors.primaryAccountAdminEmail !== ''"
            class="absolute right-0 top-0 mr-3 mt-2.5"
            xmlns="http://www.w3.org/2000/svg"
            width="17.433"
            height="15.57"
            viewBox="0 0 17.433 15.57"
          >
            <path
              class="a"
              fill="red"
              d="M19.066,17.847,12.211,5.978h0a1.808,1.808,0,0,0-.342-.483,1.772,1.772,0,0,0-2.505,0,1.715,1.715,0,0,0-.307.414h0L2.2,17.781v0a1.727,1.727,0,0,0-.246.535,1.769,1.769,0,0,0,1.764,2.229l13.707,0h0a1.77,1.77,0,0,0,1.636-2.7Zm-7.64.057a1.2,1.2,0,0,1-1.661,0,1.072,1.072,0,0,1-.354-.8,1.087,1.087,0,0,1,.354-.807,1.182,1.182,0,0,1,1.661,0,1.086,1.086,0,0,1,.354.807A1.07,1.07,0,0,1,11.426,17.9Zm.283-6.941c-.059.278-.136.6-.226.977-.086.352-.187.792-.3,1.32s-.229,1.178-.354,1.953h-.475q-.185-1.175-.355-1.958t-.3-1.311c-.079-.323-.15-.639-.216-.951a3.652,3.652,0,0,1-.1-.7,1.076,1.076,0,0,1,.351-.808,1.237,1.237,0,0,1,1.693,0,1.067,1.067,0,0,1,.362.808A3.393,3.393,0,0,1,11.709,10.963Z"
              transform="translate(-1.898 -4.978)"
            />
          </svg>
          <span
            v-if="errors.primaryAccountAdminEmail !== ''"
            class="right-0 absolute mr-1 text-red text-xxs capitalize text-right block w-full"
            >{{ errors.primaryAccountAdminEmail }}
          </span>
        </div>
      </div>
    </div>
    <div class="text-left">
      <fieldset class="mt-8 mb-4 flex flex-col items-start -mx-2">
        <legend class="text-gray">Add other admins?</legend>
        <div class="flex mt-3">
          <div class="flex items-center mr-4">
            <input
              id="yes-other-admins"
              v-model="hasOtherAdmins"
              type="radio"
              class="checkbox mr-2"
              name="has-other-admins"
              :value="1"
            />
            <label
              class="inline-flex items-center cursor-pointer"
              for="yes-other-admins"
            >
              Yes</label
            >
          </div>
          <div class="flex items-center mr-5">
            <input
              id="no-other-admins"
              v-model="hasOtherAdmins"
              type="radio"
              class="checkbox mr-2"
              name="has-other-admins"
              :value="0"
            />
            <label
              class="inline-flex items-center cursor-pointer"
              for="no-other-admins"
            >
              No</label
            >
          </div>
        </div>
      </fieldset>
    </div>
    <div v-show="hasOtherAdmins" class="text-left">
      <fieldset
        v-for="(admin, index) in otherAdmins"
        :key="index"
        class="mt-8 flex -mx-2"
      >
        <legend v-show="index === 0" class="mb-3 text-gray">Admin Info:</legend>
        <div class="w-1/2 flex-grow pr-4 relative">
          <input
            v-model="admin.adminName"
            type="text"
            name="admin-name"
            placeholder="Name"
            class="input"
            :class="[
              otherAdminsErrors[index].adminName !== '' ? 'border-red' : '',
            ]"
            @input="clearOtherAdminsErrors(index, true)"
          />
          <svg
            v-if="otherAdminsErrors[index].adminName !== ''"
            class="absolute right-0 top-0 mr-7 mt-2.5"
            xmlns="http://www.w3.org/2000/svg"
            width="17.433"
            height="15.57"
            viewBox="0 0 17.433 15.57"
          >
            <path
              class="a"
              fill="red"
              d="M19.066,17.847,12.211,5.978h0a1.808,1.808,0,0,0-.342-.483,1.772,1.772,0,0,0-2.505,0,1.715,1.715,0,0,0-.307.414h0L2.2,17.781v0a1.727,1.727,0,0,0-.246.535,1.769,1.769,0,0,0,1.764,2.229l13.707,0h0a1.77,1.77,0,0,0,1.636-2.7Zm-7.64.057a1.2,1.2,0,0,1-1.661,0,1.072,1.072,0,0,1-.354-.8,1.087,1.087,0,0,1,.354-.807,1.182,1.182,0,0,1,1.661,0,1.086,1.086,0,0,1,.354.807A1.07,1.07,0,0,1,11.426,17.9Zm.283-6.941c-.059.278-.136.6-.226.977-.086.352-.187.792-.3,1.32s-.229,1.178-.354,1.953h-.475q-.185-1.175-.355-1.958t-.3-1.311c-.079-.323-.15-.639-.216-.951a3.652,3.652,0,0,1-.1-.7,1.076,1.076,0,0,1,.351-.808,1.237,1.237,0,0,1,1.693,0,1.067,1.067,0,0,1,.362.808A3.393,3.393,0,0,1,11.709,10.963Z"
              transform="translate(-1.898 -4.978)"
            />
          </svg>
          <span
            v-if="otherAdminsErrors[index].adminName !== ''"
            class="right-0 absolute mr-5 text-red text-xxs capitalize text-right block w-full"
            >{{ otherAdminsErrors[index].adminName }}
          </span>
        </div>
        <div class="w-1/2 flex-grow pl-4 relative">
          <input
            v-model="admin.adminEmail"
            type="text"
            name="admin-email"
            placeholder="Email"
            class="input"
            :class="[
              otherAdminsErrors[index].adminEmail !== '' ? 'border-red' : '',
            ]"
            @input="clearOtherAdminsErrors(index, false)"
          />
          <svg
            v-if="otherAdminsErrors[index].adminEmail !== ''"
            class="absolute right-0 top-0 mr-7 mt-2.5"
            xmlns="http://www.w3.org/2000/svg"
            width="17.433"
            height="15.57"
            viewBox="0 0 17.433 15.57"
          >
            <path
              class="a"
              fill="red"
              d="M19.066,17.847,12.211,5.978h0a1.808,1.808,0,0,0-.342-.483,1.772,1.772,0,0,0-2.505,0,1.715,1.715,0,0,0-.307.414h0L2.2,17.781v0a1.727,1.727,0,0,0-.246.535,1.769,1.769,0,0,0,1.764,2.229l13.707,0h0a1.77,1.77,0,0,0,1.636-2.7Zm-7.64.057a1.2,1.2,0,0,1-1.661,0,1.072,1.072,0,0,1-.354-.8,1.087,1.087,0,0,1,.354-.807,1.182,1.182,0,0,1,1.661,0,1.086,1.086,0,0,1,.354.807A1.07,1.07,0,0,1,11.426,17.9Zm.283-6.941c-.059.278-.136.6-.226.977-.086.352-.187.792-.3,1.32s-.229,1.178-.354,1.953h-.475q-.185-1.175-.355-1.958t-.3-1.311c-.079-.323-.15-.639-.216-.951a3.652,3.652,0,0,1-.1-.7,1.076,1.076,0,0,1,.351-.808,1.237,1.237,0,0,1,1.693,0,1.067,1.067,0,0,1,.362.808A3.393,3.393,0,0,1,11.709,10.963Z"
              transform="translate(-1.898 -4.978)"
            />
          </svg>
          <span
            v-if="otherAdminsErrors[index].adminEmail !== ''"
            class="right-0 absolute mr-5 text-red text-xxs capitalize text-right block w-full"
            >{{ otherAdminsErrors[index].adminEmail }}
          </span>
        </div>
      </fieldset>
      <button class="-mx-2 text-blue mt-4" @click="addOtherAdmin"
        >+ Add Another</button
      >
    </div>
    <div class="mt-8 mb-4">
      <button
        class="primary-button"
        :disabled="isTryingToSetupAdminsOrganization"
        @click="submitAdminsForm"
      >
        <span v-if="isTryingToSetupAdminsOrganization">Loading</span>
        <span v-if="!isTryingToSetupAdminsOrganization">Finish</span>
      </button>
    </div>
  </div>
</template>
